import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import TodayIcon from '@mui/icons-material/Today';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import { auditLogService, AuditLog, AuditLogFilters, AuditLogStats } from '../services/auditLogService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import JalaliDatePicker from '../components/common/JalaliDatePicker';
import moment from 'moment-jalaali';
import './EntityManagement.css';

const formatJalali = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD HH:mm'));
  } catch {
    return String(date);
  }
};

const actionTagClass: Record<string, string> = {
  create: 'tag-active',
  update: 'tag-platform',
  delete: 'tag-inactive',
  restore: 'tag-platform',
  login: 'tag-active',
  logout: 'tag-platform',
  view: 'tag-platform',
  api_request: 'tag-platform',
};

const entityTypes = [
  { value: '', label: 'همه' },
  { value: 'User', label: 'کاربر' },
  { value: 'Product', label: 'محصول' },
  { value: 'Order', label: 'سفارش' },
  { value: 'Store', label: 'فروشگاه' },
  { value: 'Role', label: 'نقش' },
  { value: 'Setting', label: 'تنظیمات' },
  { value: 'Ticket', label: 'تیکت' },
  { value: 'Chat', label: 'چت' },
  { value: 'Message', label: 'پیام' },
  { value: 'BankAccount', label: 'حساب بانکی' },
  { value: 'SellerApplication', label: 'درخواست فروشنده' },
  { value: 'KycVerification', label: 'احراز هویت' },
  { value: 'BannerSlot', label: 'جایگاه بنر' },
  { value: 'DiscountCode', label: 'کد تخفیف' },
  { value: 'ProductReview', label: 'نظر محصول' },
  { value: 'AccountType', label: 'نوع حساب' },
  { value: 'ActivityArea', label: 'حوزه فعالیت' },
  { value: 'HttpRequest', label: 'درخواست HTTP' },
];

const actionTypes = [
  { value: '', label: 'همه' },
  { value: 'create', label: 'ایجاد' },
  { value: 'update', label: 'ویرایش' },
  { value: 'delete', label: 'حذف' },
  { value: 'view', label: 'مشاهده' },
  { value: 'login', label: 'ورود' },
  { value: 'logout', label: 'خروج' },
  { value: 'restore', label: 'بازیابی' },
  { value: 'api_request', label: 'درخواست HTTP' },
];

const AuditLogsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Filters
  const [activeFilters, setActiveFilters] = useState<AuditLogFilters>({});
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterEntityId, setFilterEntityId] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterIp, setFilterIp] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Helper function to format field values for display
  const formatFieldValue = (field: string, value: any): React.ReactNode => {
    if (value === null || value === undefined) return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>خالی</span>;

    if (field.toLowerCase().includes('image') || field.toLowerCase().includes('avatar') || field.toLowerCase().includes('logo') || field.toLowerCase().includes('picture')) {
      if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('/uploads'))) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={value} alt="تصویر" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e2e8f0' }} />
            <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#B25373', fontSize: '11px', textDecoration: 'none' }}>
              مشاهده تصویر
            </a>
          </div>
        );
      }
    }

    if (typeof value === 'boolean') {
      return (
        <span className={`tag tag-sm ${value ? 'tag-active' : 'tag-inactive'}`}>
          {value ? 'بله' : 'خیر'}
        </span>
      );
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      try {
        return <span className="cell-code">{formatJalali(value)}</span>;
      } catch {
        return value;
      }
    }

    if (typeof value === 'number') {
      return <span className="cell-code">{toPersianDigits(value.toLocaleString('en-US'))}</span>;
    }

    const statusFields = ['status', 'verificationStatus', 'paymentStatus', 'orderStatus'];
    if (statusFields.some(f => field.toLowerCase().includes(f.toLowerCase())) && typeof value === 'string') {
      return <span className="tag tag-sm tag-platform">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>خالی</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {value.map((item, idx) => (
            <span key={idx} className="tag tag-sm tag-platform">
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </span>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <pre style={{
          margin: 0,
          padding: '8px',
          background: '#f8fafc',
          borderRadius: '6px',
          fontSize: '11px',
          fontFamily: 'Consolas, Monaco, monospace',
          maxWidth: '300px',
          overflow: 'auto',
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (typeof value === 'string' && value.length > 100) {
      return (
        <div style={{ maxWidth: '300px', wordBreak: 'break-word', fontSize: '12px', lineHeight: '1.5' }}>
          {value}
        </div>
      );
    }

    return <span style={{ fontSize: '13px' }}>{String(value)}</span>;
  };

  // Helper to compare two states and extract differences
  const compareStates = (previousState: any, newState: any) => {
    const differences: Array<{ field: string; oldValue: any; newValue: any }> = [];

    if (!previousState && !newState) return differences;

    if (!previousState) {
      const allFields = Object.keys(newState || {});
      allFields.forEach(field => {
        if (!['_id', '__v', 'createdAt', 'updatedAt'].includes(field)) {
          differences.push({ field, oldValue: null, newValue: newState[field] });
        }
      });
      return differences;
    }

    if (!newState) {
      const allFields = Object.keys(previousState || {});
      allFields.forEach(field => {
        if (!['_id', '__v', 'createdAt', 'updatedAt'].includes(field)) {
          differences.push({ field, oldValue: previousState[field], newValue: null });
        }
      });
      return differences;
    }

    const allFields = new Set([...Object.keys(previousState), ...Object.keys(newState)]);

    allFields.forEach(field => {
      if (['_id', '__v', 'createdAt', 'updatedAt'].includes(field)) return;

      const oldValue = previousState[field];
      const newValue = newState[field];

      const oldStr = JSON.stringify(oldValue);
      const newStr = JSON.stringify(newValue);

      if (oldStr !== newStr) {
        differences.push({ field, oldValue, newValue });
      }
    });

    return differences;
  };

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await auditLogService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditLogService.getLogs(activeFilters, page, rowsPerPage);
      setLogs(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      setError('خطا در بارگذاری لاگ‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, page, rowsPerPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSearch = () => {
    const newFilters: AuditLogFilters = {};
    if (filterEntityType) newFilters.entityType = filterEntityType;
    if (filterEntityId) newFilters.entityId = filterEntityId;
    if (filterAction) newFilters.action = filterAction;
    if (filterIp) newFilters.ip = filterIp;
    if (filterStartDate) newFilters.startDate = filterStartDate;
    if (filterEndDate) newFilters.endDate = filterEndDate;
    if (filterUserId) newFilters.userId = filterUserId;
    if (searchInput) newFilters.entityId = searchInput;
    setActiveFilters(newFilters);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClearFilters = () => {
    setFilterEntityType('');
    setFilterEntityId('');
    setFilterAction('');
    setFilterIp('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterUserId('');
    setSearchInput('');
    setActiveFilters({});
    setPage(1);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await auditLogService.exportLogs(activeFilters, 'csv');
    } catch (err) {
      setError('خطا در خروجی گرفتن');
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getActionLabel = (action: string) => auditLogService.getActionDisplayName(action);
  const getEntityTypeLabel = (type: string) => auditLogService.getEntityTypeDisplayName(type);

  const getDescription = (log: AuditLog) => {
    if (log.description) return log.description;
    if (log.action === 'api_request' && log.metadata) {
      return `${log.metadata.method} ${log.metadata.route} (${log.metadata.statusCode || ''})`;
    }
    return `${getActionLabel(log.action)} ${getEntityTypeLabel(log.entityType)}`;
  };

  const hasActiveFilters = !!(filterEntityType || filterEntityId || filterAction || filterIp || filterStartDate || filterEndDate || filterUserId || searchInput);

  const totalPages = Math.ceil(total / rowsPerPage);
  const startItem = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, total);

  // Stats computations
  const totalToday = stats?.totalToday || 0;
  const totalEvents = stats ? (stats.totalThisMonth || 0) : total;
  const deleteCount = stats?.byAction?.find(a => a.action === 'delete')?.count || 0;
  const apiRequestCount = stats?.byAction?.find(a => a.action === 'api_request')?.count || 0;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">لاگ‌های سیستم</h1>
          <p className="entity-subtitle">تاریخچه فعالیت‌ها و تغییرات سیستم</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn"
            onClick={handleExport}
            disabled={exportLoading}
            title="خروجی CSV"
          >
            {exportLoading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
            خروجی CSV
          </button>
          <button
            className="header-btn refresh"
            onClick={() => { loadLogs(); loadStats(); }}
            disabled={loading}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {error && (
        <div className="dialog-error" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>{'\u2715'}</button>
        </div>
      )}

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><HistoryIcon /></div>
          <div>
            <p className="entity-stat-label">کل رویدادها</p>
            <p className="entity-stat-value">
              {statsLoading ? '...' : toPersianDigits(totalEvents.toLocaleString('en-US'))}
            </p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><TodayIcon /></div>
          <div>
            <p className="entity-stat-label">رویدادهای امروز</p>
            <p className="entity-stat-value">
              {statsLoading ? '...' : toPersianDigits(totalToday.toLocaleString('en-US'))}
            </p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><WarningAmberIcon /></div>
          <div>
            <p className="entity-stat-label">درخواست‌های HTTP</p>
            <p className="entity-stat-value">
              {statsLoading ? '...' : toPersianDigits(apiRequestCount.toLocaleString('en-US'))}
            </p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><ErrorOutlineIcon /></div>
          <div>
            <p className="entity-stat-label">حذف‌ها</p>
            <p className="entity-stat-value">
              {statsLoading ? '...' : toPersianDigits(deleteCount.toLocaleString('en-US'))}
            </p>
          </div>
        </div>
      </div>

      {/* Breakdown: Actions + Entity Types */}
      {stats && !statsLoading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14,
          marginBottom: 16,
        }}>
          {/* By Action */}
          <div style={{
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-divider)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px',
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-text-primary)',
              background: 'var(--color-bg-default)',
              borderBottom: '1px solid var(--color-divider)',
            }}>
              بر اساس نوع عملیات
            </div>
            <div style={{ padding: '4px 12px' }}>
              {stats.byAction.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  داده‌ای موجود نیست
                </div>
              ) : (
                stats.byAction.slice(0, 8).map((item) => (
                  <div key={item.action} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 4px',
                    borderBottom: '1px solid var(--color-divider)',
                  }}>
                    <span className={`tag tag-sm ${actionTagClass[item.action] || 'tag-platform'}`}>
                      {getActionLabel(item.action)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      {toPersianDigits(item.count.toLocaleString('en-US'))}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* By Entity Type */}
          <div style={{
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-divider)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px',
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-text-primary)',
              background: 'var(--color-bg-default)',
              borderBottom: '1px solid var(--color-divider)',
            }}>
              بر اساس نوع موجودیت
            </div>
            <div style={{ padding: '4px 12px' }}>
              {stats.byEntityType.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  داده‌ای موجود نیست
                </div>
              ) : (
                stats.byEntityType.slice(0, 8).map((item) => (
                  <div key={item.entityType} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 4px',
                    borderBottom: '1px solid var(--color-divider)',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                      {getEntityTypeLabel(item.entityType)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      {toPersianDigits(item.count.toLocaleString('en-US'))}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Users + Top IPs */}
      {stats && !statsLoading && (stats.topUsers.length > 0 || stats.topIPs.length > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14,
          marginBottom: 16,
        }}>
          {/* Top Users */}
          <div style={{
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-divider)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px',
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-text-primary)',
              background: 'var(--color-bg-default)',
              borderBottom: '1px solid var(--color-divider)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>👤</span> فعال‌ترین کاربران
            </div>
            <div style={{ padding: '4px 12px' }}>
              {stats.topUsers.slice(0, 5).map((user, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 4px',
                  borderBottom: '1px solid var(--color-divider)',
                }}>
                  <span style={{
                    fontSize: 12, color: 'var(--color-text-primary)', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200,
                  }}>
                    {user.fullName || user.userId}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', flexShrink: 0 }}>
                    {toPersianDigits(user.count.toLocaleString('en-US'))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top IPs */}
          <div style={{
            background: 'var(--color-bg-paper)',
            border: '1px solid var(--color-divider)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px',
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-text-primary)',
              background: 'var(--color-bg-default)',
              borderBottom: '1px solid var(--color-divider)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>🌐</span> پرتکرارترین IPها
            </div>
            <div style={{ padding: '4px 12px' }}>
              {stats.topIPs.slice(0, 5).map((item, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 4px',
                  borderBottom: '1px solid var(--color-divider)',
                }}>
                  <span className="tag tag-sm tag-platform" style={{ fontFamily: 'monospace', direction: 'ltr' }}>
                    {item.ip}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>
                    {toPersianDigits(item.count.toLocaleString('en-US'))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="شناسه موجودیت..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع عملیات</label>
            <select
              className="filter-field"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
            >
              {actionTypes.map((at) => (
                <option key={at.value} value={at.value}>
                  {at.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع موجودیت</label>
            <select
              className="filter-field"
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
            >
              {entityTypes.map((et) => (
                <option key={et.value} value={et.value}>
                  {et.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">شناسه کاربر</label>
            <input
              className="filter-field"
              type="text"
              placeholder="User ID"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">آدرس IP</label>
            <input
              className="filter-field"
              type="text"
              placeholder="192.168.1.1"
              value={filterIp}
              onChange={(e) => setFilterIp(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">از تاریخ</label>
            <JalaliDatePicker
              value={filterStartDate}
              onChange={setFilterStartDate}
              placeholder="تاریخ شروع"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">تا تاریخ</label>
            <JalaliDatePicker
              value={filterEndDate}
              onChange={setFilterEndDate}
              placeholder="تاریخ پایان"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={handleClearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {filterAction && (
              <span className="filter-tag">
                عملیات: {getActionLabel(filterAction)}
                <button onClick={() => setFilterAction('')}>×</button>
              </span>
            )}
            {filterEntityType && (
              <span className="filter-tag">
                موجودیت: {getEntityTypeLabel(filterEntityType)}
                <button onClick={() => setFilterEntityType('')}>×</button>
              </span>
            )}
            {filterUserId && (
              <span className="filter-tag">
                کاربر: {filterUserId}
                <button onClick={() => setFilterUserId('')}>×</button>
              </span>
            )}
            {filterIp && (
              <span className="filter-tag">
                IP: {filterIp}
                <button onClick={() => setFilterIp('')}>×</button>
              </span>
            )}
            {searchInput && (
              <span className="filter-tag">
                شناسه: {searchInput}
                <button onClick={() => setSearchInput('')}>×</button>
              </span>
            )}
            {filterStartDate && (
              <span className="filter-tag">
                از: {toPersianDigits(moment(filterStartDate).format('jYYYY/jMM/jDD'))}
                <button onClick={() => setFilterStartDate('')}>×</button>
              </span>
            )}
            {filterEndDate && (
              <span className="filter-tag">
                تا: {toPersianDigits(moment(filterEndDate).format('jYYYY/jMM/jDD'))}
                <button onClick={() => setFilterEndDate('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={handleClearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : logs.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><DescriptionIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ لاگی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>عملیات</th>
                  <th>موجودیت</th>
                  <th>توضیحات</th>
                  <th>کاربر</th>
                  <th>IP</th>
                  <th style={{ width: 80 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <span className="cell-code">{formatJalali(log.metadata?.timestamp || log.timestamp)}</span>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${actionTagClass[log.action] || 'tag-platform'}`}>
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 13 }}>{getEntityTypeLabel(log.entityType)}</span>
                    </td>
                    <td>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--color-text-secondary)',
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {getDescription(log)}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: 13 }}>
                        {log.performedBy?.fullName || log.userId?.fullName || log.userId?.name || 'سیستم'}
                      </span>
                    </td>
                    <td>
                      <span className="cell-code">{log.metadata?.ip || '-'}</span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleViewDetails(log)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {logs.map((log) => (
                <div key={log._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image"><HistoryIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">
                        {getActionLabel(log.action)} — {getEntityTypeLabel(log.entityType)}
                      </div>
                      <div className="entity-card-desc">
                        {formatJalali(log.metadata?.timestamp || log.timestamp)}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${actionTagClass[log.action] || 'tag-platform'}`}>
                        {getActionLabel(log.action)}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {getDescription(log)}
                  </div>
                  <div style={{ padding: '4px 14px 8px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span className="tag tag-sm tag-platform">
                      {log.performedBy?.fullName || log.userId?.fullName || log.userId?.name || 'سیستم'}
                    </span>
                    {log.metadata?.ip && (
                      <span className="cell-code">{log.metadata.ip}</span>
                    )}
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleViewDetails(log)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(startItem.toString())} تا {toPersianDigits(endItem.toString())} از {toPersianDigits(total.toString())}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(page.toString())} / {toPersianDigits((totalPages || 1).toString())}
                </span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        className="auditlog-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات لاگ</span>
          <button className="details-close" onClick={() => setDetailDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedLog && (
            <div className="edit-form">
              {/* Event Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات رویداد</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>تاریخ</label>
                      <div className="cell-code" style={{ padding: '8px 0' }}>
                        {formatJalali(selectedLog.metadata?.timestamp || selectedLog.timestamp)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>عملیات</label>
                      <div style={{ padding: '8px 0' }}>
                        <span className={`tag tag-sm ${actionTagClass[selectedLog.action] || 'tag-platform'}`}>
                          {getActionLabel(selectedLog.action)}
                        </span>
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>نوع موجودیت</label>
                      <div style={{ padding: '8px 0', fontSize: 13 }}>
                        {getEntityTypeLabel(selectedLog.entityType)}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>شناسه موجودیت</label>
                      <div className="cell-code" style={{ padding: '8px 0', wordBreak: 'break-all', fontSize: 11 }}>
                        {selectedLog.entityId || '-'}
                      </div>
                    </div>
                    {selectedLog.description && (
                      <div className="edit-field full">
                        <label>توضیحات</label>
                        <div style={{ padding: '8px 0', fontSize: 13 }}>{selectedLog.description}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* User Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات کاربر</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>کاربر</label>
                      <div style={{ padding: '8px 0', fontSize: 13 }}>
                        {selectedLog.performedBy?.fullName || selectedLog.userId?.fullName || selectedLog.userId?.name || 'سیستم'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>آدرس IP</label>
                      <div className="cell-code" style={{ padding: '8px 0' }}>
                        {selectedLog.metadata?.ip || '-'}
                      </div>
                    </div>
                    {selectedLog.metadata?.userAgent && (
                      <div className="edit-field full">
                        <label>User Agent</label>
                        <div style={{ padding: '8px 0', fontSize: 11, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {selectedLog.metadata.userAgent}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Request Info (for api_request) */}
              {selectedLog.metadata?.route && (
                <div className="edit-card">
                  <div className="edit-card-header">اطلاعات درخواست</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field full">
                        <label>مسیر</label>
                        <div className="cell-code" style={{ padding: '8px 0', fontSize: 12 }}>
                          <span style={{ color: '#6366f1', fontWeight: 600 }}>{selectedLog.metadata.method}</span> {selectedLog.metadata.route}
                        </div>
                      </div>
                      {selectedLog.metadata.statusCode !== undefined && (
                        <div className="edit-field">
                          <label>کد وضعیت</label>
                          <div style={{ padding: '8px 0' }}>
                            <span className={`tag tag-sm ${selectedLog.metadata.statusCode < 400 ? 'tag-active' : 'tag-inactive'}`}>
                              {toPersianDigits(String(selectedLog.metadata.statusCode))}
                            </span>
                          </div>
                        </div>
                      )}
                      {selectedLog.metadata.duration != null && (
                        <div className="edit-field">
                          <label>مدت پاسخ</label>
                          <div className="cell-code" style={{ padding: '8px 0' }}>
                            {toPersianDigits(String(selectedLog.metadata.duration))} ms
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedLog.action === 'api_request' && selectedLog.newState && (
                      <div className="edit-field full" style={{ marginTop: 12 }}>
                        <label>بدنه درخواست</label>
                        <pre style={{
                          margin: '4px 0 0',
                          padding: 12,
                          background: '#f5f8ff',
                          border: '1px solid #e0e7ff',
                          borderRadius: 8,
                          fontSize: 11,
                          fontFamily: 'Consolas, Monaco, monospace',
                          maxHeight: 300,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#312e81',
                        }}>
                          {JSON.stringify(selectedLog.newState, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Changes / Diff */}
              {selectedLog.changes && (Array.isArray(selectedLog.changes) ? selectedLog.changes.length > 0 : true) && (
                <div className="edit-card">
                  <div className="edit-card-header">تغییرات فیلدها</div>
                  <div className="edit-card-body">
                    {Array.isArray(selectedLog.changes) && selectedLog.changes.length > 0 ? (
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--color-border)' }}>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#475569' }}>فیلد</th>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#dc2626' }}>مقدار قبلی</th>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#16a34a' }}>مقدار جدید</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLog.changes.map((change: any, i: number) => (
                              <tr key={i} style={{ borderBottom: i < (selectedLog.changes as any[]).length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b', background: '#f8fafc' }}>
                                  {auditLogService.getFieldDisplayName(change.field)}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: 12, fontFamily: 'Consolas, monospace', background: '#fef2f2', color: '#991b1b', wordBreak: 'break-word' }}>
                                  {typeof change.oldValue === 'object'
                                    ? JSON.stringify(change.oldValue, null, 2)
                                    : String(change.oldValue ?? '-')}
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: 12, fontFamily: 'Consolas, monospace', background: '#f0fdf4', color: '#166534', wordBreak: 'break-word' }}>
                                  {typeof change.newValue === 'object'
                                    ? JSON.stringify(change.newValue, null, 2)
                                    : String(change.newValue ?? '-')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <pre style={{
                        margin: 0,
                        padding: 12,
                        background: '#f8fafc',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 12,
                        fontFamily: 'Consolas, Monaco, monospace',
                        maxHeight: 300,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        {JSON.stringify(selectedLog.changes, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* State Comparison */}
              {(selectedLog.previousState || selectedLog.newState) && selectedLog.action !== 'api_request' && (() => {
                const diffs = compareStates(selectedLog.previousState, selectedLog.newState);
                if (diffs.length === 0) return null;
                return (
                  <div className="edit-card">
                    <div className="edit-card-header">
                      مقایسه وضعیت ({toPersianDigits(diffs.length.toString())} فیلد تغییر یافته)
                    </div>
                    <div className="edit-card-body">
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--color-border)' }}>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#475569', width: '20%' }}>فیلد</th>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#dc2626', width: '40%' }}>وضعیت قبلی</th>
                              <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#16a34a', width: '40%' }}>وضعیت جدید</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diffs.map((diff, i) => (
                              <tr key={i} style={{ borderBottom: i < diffs.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b', background: '#f8fafc', verticalAlign: 'top' }}>
                                  {auditLogService.getFieldDisplayName(diff.field)}
                                </td>
                                <td style={{ padding: '10px 12px', background: '#fef2f2', verticalAlign: 'top', borderLeft: '3px solid #fca5a5' }}>
                                  {formatFieldValue(diff.field, diff.oldValue)}
                                </td>
                                <td style={{ padding: '10px 12px', background: '#f0fdf4', verticalAlign: 'top', borderLeft: '3px solid #86efac' }}>
                                  {formatFieldValue(diff.field, diff.newValue)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDetailDialogOpen(false)}>بستن</button>
        </div>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
